import { Bell, Check, ChevronDown, ChevronUp, Pencil, Trash2, Undo2 } from "lucide-react";
import { getServiceLabel } from "../data/services";
import { formatTimeRange } from "../lib/date";
import type { Appointment, Owner, Pet } from "../types";

interface AppointmentCardProps {
  appointment: Appointment;
  pet: Pet;
  owner: Owner;
  expanded: boolean;
  onToggle(): void;
  onRemind(): void;
  onToggleStatus(): void;
  onEdit(): void;
  onDelete(): void;
}

const sexLabel = { male: "on", female: "ona", unknown: "" };

export function AppointmentCard({
  appointment,
  pet,
  owner,
  expanded,
  onToggle,
  onRemind,
  onToggleStatus,
  onEdit,
  onDelete,
}: AppointmentCardProps) {
  const confirmed = appointment.status === "confirmed";
  const serviceLabel = getServiceLabel(appointment.serviceType, appointment.customServiceName);
  const servicePrice = appointment.servicePricePln === undefined ? null : new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }).format(appointment.servicePricePln);

  function handleDelete(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (window.confirm(`Usunąć wizytę ${pet.name} (${formatTimeRange(appointment.startsAt, appointment.endsAt)})?`)) {
      onDelete();
    }
  }

  function stopAndRun(callback: () => void) {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      callback();
    };
  }

  return (
    <article className={`appointment-card ${expanded ? "appointment-card--expanded" : ""}`} onClick={onToggle}>
      <div className="appointment-card__summary">
        <div className="pet-avatar" aria-hidden="true">{pet.avatar || "🐾"}</div>
        <div className="appointment-card__main">
          <div className="appointment-card__title-row">
            <h2>{pet.name}</h2>
            {sexLabel[pet.sex] && <span>{sexLabel[pet.sex]}</span>}
          </div>
          <p>{owner.fullName}</p>
          <strong>{formatTimeRange(appointment.startsAt, appointment.endsAt)}</strong>
        </div>
        <div className="appointment-card__right">
          <span className={`status-badge status-badge--${appointment.status}`}>
            {confirmed ? "Potwierdzona" : "Czeka"}
          </span>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {expanded && (
        <div className="appointment-card__details" onClick={(event) => event.stopPropagation()}>
          {pet.breed && <p className="appointment-card__breed">{pet.breed}</p>}
          <p className="appointment-card__service">{serviceLabel}{servicePrice && <strong> · {servicePrice}</strong>}</p>
          {appointment.notes && <p className="appointment-card__note">{appointment.notes}</p>}
          {pet.careNotes && <p className="appointment-card__care-note">🐾 {pet.careNotes}</p>}
          <div className="appointment-actions">
            <button className="icon-button" type="button" title="Przypomnij" aria-label="Przypomnij" onClick={stopAndRun(onRemind)}>
              <Bell size={20} />
            </button>
            <button
              className="icon-button"
              type="button"
              title={confirmed ? "Cofnij potwierdzenie" : "Potwierdź wizytę"}
              aria-label={confirmed ? "Cofnij potwierdzenie" : "Potwierdź wizytę"}
              onClick={stopAndRun(onToggleStatus)}
            >
              {confirmed ? <Undo2 size={20} /> : <Check size={20} />}
            </button>
            <button className="icon-button" type="button" title="Edytuj" aria-label="Edytuj" onClick={stopAndRun(onEdit)}>
              <Pencil size={20} />
            </button>
            <button className="icon-button icon-button--danger" type="button" title="Usuń" aria-label="Usuń" onClick={handleDelete}>
              <Trash2 size={20} />
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
